// Escape-key handler with a LIFO stack so nested/overlapping modals don't
// both close on a single press — only the topmost handler fires.

import { useEffect } from 'react';

type Handler = () => void;

const stack: Handler[] = [];
let attached = false;

function onWindowKey(e: KeyboardEvent) {
  if (e.key !== 'Escape') return;
  const top = stack[stack.length - 1];
  if (top) {
    e.preventDefault();
    top();
  }
}

function ensureListener() {
  if (attached) return;
  window.addEventListener('keydown', onWindowKey);
  attached = true;
}

function maybeDetachListener() {
  if (stack.length === 0 && attached) {
    window.removeEventListener('keydown', onWindowKey);
    attached = false;
  }
}

export function useEscapeKey(handler: Handler, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    ensureListener();
    stack.push(handler);
    return () => {
      const idx = stack.lastIndexOf(handler);
      if (idx !== -1) stack.splice(idx, 1);
      maybeDetachListener();
    };
  }, [active, handler]);
}
